import { DwLoggingService, DwMetaDataFormApi } from "@devwareapps/devware-cap";
import { AppMetaData, CheckRideRequestEntity, CheckRideRequestSubStatusAllItems, CheckRideScheduleHoldEntity } from "../../../meta-data/app-meta-data.service";
import { ExaminerRepositoryService } from "../../examiner/services/examiner-repository.service";
import { combineLatest, Observable, of, Subject } from "rxjs";
import { ScheduleHoldResult, ScheduleHoldResultAction } from "../../examiner/models/schedule-hold-result.model";
import { ScheduleHoldRequest, ScheduleHoldRequestAction } from "../../examiner/models/schedule-hold-request.model";
import { mergeMap, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { Router } from "@angular/router";
import { ExaminerBookingVariableContext } from "../models/examiner-booking-variable-context.model";

@Injectable({ providedIn: 'root' })
export class ScheduleHoldUtilService {

    private static LastScheduleHold: CheckRideScheduleHoldEntity = null;

    private useLogging = true;

    constructor(private examinerRespositoryService: ExaminerRepositoryService, private dateTimeUtilService: DateTimeUtilService, private router: Router, private dwLoggingService: DwLoggingService) { }

    private scheduleHoldSubjectChanged: Subject<any> = new Subject<any>();

    public getScheduleHoldSubjectChanged(): Observable<any> {
        return this.scheduleHoldSubjectChanged.asObservable();
    }

    public requiresScheduleHold(checkRideRequest: CheckRideRequestEntity, checkStatus?: boolean): boolean {
        const correctPath = (this.router.url.indexOf('/checkrides') >= 0) || (this.router.url.indexOf('/school-checkrides') >= 0);

        if (!correctPath) {
            return false;
        }

        if (!checkStatus) {
            return true;
        }

        if (!checkRideRequest.ScheduledPilotExaminerId) {
            return false;
        }

        switch(checkRideRequest.CheckRideRequestSubStatusId) {
            case CheckRideRequestSubStatusAllItems.PendingApproval:
            case CheckRideRequestSubStatusAllItems.PendingApprovalPayment:
            case CheckRideRequestSubStatusAllItems.Draft:
            case CheckRideRequestSubStatusAllItems.PendingPayment:
                return true;

            default:
                if (!checkRideRequest.CheckRideRequestSubStatusId) {
                    return true;
                }
                return false;
        }
    }

    public startScheduleHoldMultiple(checkrideRequest: CheckRideRequestEntity[]): Observable<ScheduleHoldResult[]> {
        const obs: Observable<ScheduleHoldResult>[] = [];

        for (const checkride of checkrideRequest) {
            obs.push(this.startScheduleHold(checkride, false, false));
        }

        return combineLatest(obs).pipe(tap((results: ScheduleHoldResult[]) => {
            if (this.useLogging) {
                this.dwLoggingService.logVerbose(`ScheduleHoldUtilService.startScheduleHoldMultiple - Complete`, results);
            }

            return results;
        }));
    }

    public startScheduleHold(checkrideRequest: CheckRideRequestEntity, useExistingHold: boolean = true, setScheduleHold: boolean = true): Observable<ScheduleHoldResult> {
        const scheduleHold = AppMetaData.CheckRideScheduleHold.CreateEntity();

        scheduleHold.CheckRideRequestId = checkrideRequest.CheckRideRequestId;
        scheduleHold.AirportId = checkrideRequest.ScheduledAirportId;
        scheduleHold.PilotExaminerId = checkrideRequest.ScheduledPilotExaminerId;
        scheduleHold.CheckRideDateTime = checkrideRequest.ScheduledDateTime;
        scheduleHold.DurationMinutes = checkrideRequest.ScheduledTimeMinutes;

        // validate schedule hold
        if (!scheduleHold.AirportId || !scheduleHold.PilotExaminerId || !scheduleHold.CheckRideDateTime || !scheduleHold.DurationMinutes) {
            this.dwLoggingService.logWarning(`ScheduleHoldUtilService.startScheduleHold - Incomplete schedule hold information`, scheduleHold);

            return of(null);
        }

        const request: ScheduleHoldRequest = {
            RequestAction: ScheduleHoldRequestAction.startHold,
            CheckRideScheduleHold: scheduleHold
        };

        var existingHold = useExistingHold ? this.getScheduleHold() : null;

        var cancelHoldOb = of(null);

        if (existingHold) {
            cancelHoldOb = this.cancelScheduleHold(existingHold, checkrideRequest);
        }

        return cancelHoldOb.pipe(mergeMap(() => {
            // Clear existing hold
            if (setScheduleHold) {
                this.setScheduleHold(null);
            }

            this.dwLoggingService.logVerbose(`ScheduleHoldUtilService.startScheduleHold - Begin`, scheduleHold);

            // Start new hold
            return this.examinerRespositoryService.scheduleCheckRideHold(request)
                .pipe(tap((result: ScheduleHoldResult) => {
                    this.dwLoggingService.logVerbose(`ScheduleHoldUtilService.startScheduleHold - Complete`, result);
                    if (result.ResultAction == ScheduleHoldResultAction.holdStarted) {
                        // store hold if successful
                        if (useExistingHold) {
                            this.setScheduleHold(result.CheckRideScheduleHold);
                        }
                    }
                }));
        }));
    }

    public extendScheduleHold(scheduleHold: CheckRideScheduleHoldEntity): Observable<ScheduleHoldResult> {
        const request: ScheduleHoldRequest = {
            RequestAction: ScheduleHoldRequestAction.extendHold,
            CheckRideScheduleHold: scheduleHold
        };

        return this.examinerRespositoryService.scheduleCheckRideHold(request)
            .pipe(tap((result: ScheduleHoldResult) => {
                if (result.ResultAction == ScheduleHoldResultAction.holdExtended) {
                    // store hold if successful
                    this.setScheduleHold(result.CheckRideScheduleHold);
                } else {
                    this.setScheduleHold(null);
                }
            }));
    }

    public cancelScheduleHold(scheduleHold: CheckRideScheduleHoldEntity, checkrideRequest: CheckRideRequestEntity): Observable<ScheduleHoldResult> {
        if (!scheduleHold) {
            return of(null);
        }

        const request: ScheduleHoldRequest = {
            RequestAction: ScheduleHoldRequestAction.cancelHold,
            CheckRideScheduleHold: scheduleHold,
        }

        if (checkrideRequest.CheckRideRequestId) {
            request.CheckRideScheduleHold.CheckRideRequestId = checkrideRequest.CheckRideRequestId;
        }

        this.dwLoggingService.logVerbose(`ScheduleHoldUtilService.cancelScheduleHold - Begin`, request);

        return this.examinerRespositoryService.scheduleCheckRideHold(request)
            .pipe(tap((result: ScheduleHoldResult) => {
                ScheduleHoldUtilService.LastScheduleHold = null;
                this.dwLoggingService.logVerbose(`ScheduleHoldUtilService.cancelScheduleHold - Complete`, result);
            }));


    }

    public getScheduleHold(): CheckRideScheduleHoldEntity {
        return ScheduleHoldUtilService.LastScheduleHold;
    }

    public setScheduleHold(scheduleHold: CheckRideScheduleHoldEntity) {
        ScheduleHoldUtilService.LastScheduleHold = scheduleHold;

        this.scheduleHoldSubjectChanged.next();
    }


    public returnToSearch(checkRideRequest: CheckRideRequestEntity, formApi: DwMetaDataFormApi, message: string): void {
        const checkRideRequestUpdate: Partial<CheckRideRequestEntity> = {
            PreferredDateTime: this.dateTimeUtilService.getCurrentDateTime(),
            ScheduledDateTime: null,
        };

        formApi.patchFormData(checkRideRequestUpdate);

        const variables = formApi.getVariableContextValue<ExaminerBookingVariableContext>();

        variables.TimeNotAvailableMessage = message || 'Your selected Checkride Date/time is no longer available. Please search for an available date/time.';

        //if (checkRideRequest?.CheckRideRequestSubStatusId != CheckRideRequestSubStatusAllItems.Rejected) {
        variables.Show_Search = true;
        variables.Preferred_Pilot_Examiner_Id = checkRideRequest.ScheduledPilotExaminerId;
        //}

        variables.Checkride_Date_No_Longer_Valid = true;

        formApi.patchVariableContext(variables);

        this.setScheduleHold(null);
    }

}